import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { useLocale } from "@/lib/i18n";

const input = "w-full border border-navy/25 bg-white px-3 py-2.5 text-[15px] text-navy rounded outline-none focus:border-navy text-start";
const primary = "inline-flex items-center gap-2 rounded bg-navy px-5 py-2.5 text-sm font-semibold text-white hover:bg-navy/90 disabled:opacity-50";
const line = "inline-flex items-center gap-2 rounded border border-navy/25 bg-white px-4 py-2 text-sm font-medium text-navy hover:border-navy";

export function Choice({ options, value, onPick }) {
  const { pick } = useLocale();
  return (
    <div className="grid gap-2" data-testid="interview-choices">
      {options.map((o) => (
        <button key={o.value} onClick={() => onPick(o.value)} data-testid={`choice-${o.value}`}
          className={`rounded border px-4 py-3 text-start text-[15px] ${value === o.value ? "border-2 border-navy bg-softcream font-semibold text-navy" : "border-navy/20 bg-white text-navy hover:border-navy"}`}>
          {pick(o.label)}
        </button>
      ))}
    </div>
  );
}

export function YesNo({ value, onPick }) {
  const { t } = useLocale();
  return (
    <div className="grid max-w-sm grid-cols-2 gap-2" data-testid="interview-yesno">
      {[[true, "yes"], [false, "no"]].map(([v, k]) => (
        <button key={k} onClick={() => onPick(v)} data-testid={`yesno-${k}`}
          className={`rounded border px-4 py-3 text-[15px] ${value === v ? "border-2 border-navy bg-softcream font-semibold text-navy" : "border-navy/20 bg-white text-navy hover:border-navy"}`}>{t(k)}</button>
      ))}
    </div>
  );
}

function Field({ f, value, onChange, tid }) {
  const { pick } = useLocale();
  if (f.type === "choice") return (
    <select className={input} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid}>
      <option value="">—</option>{f.options.map((o) => <option key={o.value} value={o.value}>{pick(o.label)}</option>)}
    </select>);
  return <input type={f.type === "date" ? "date" : "text"} className={input} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} />;
}

export function Fields({ fields, value = {}, onChange, prefix }) {
  const { pick } = useLocale();
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {fields.map((f) => (
        <div key={f.key}>
          <label className="mb-1 block text-xs font-semibold text-muted">{pick(f.label)}</label>
          <Field f={f} value={value[f.key]} onChange={(v) => onChange({ ...value, [f.key]: v })} tid={`${prefix}-${f.key}`} />
        </div>
      ))}
    </div>
  );
}

export function Repeat({ q, value, onSubmit, busy }) {
  const { t, pick } = useLocale();
  const [rows, setRows] = useState(value || []);
  const [draft, setDraft] = useState({});
  const addRow = () => { if (Object.values(draft).some(Boolean)) { setRows([...rows, draft]); setDraft({}); } };
  const first = q.fields[0].key;
  return (
    <div data-testid="interview-repeat">
      {rows.map((r, i) => (
        <div key={i} className="mb-2 flex items-center justify-between border border-navy/15 bg-softcream px-4 py-2.5 text-sm text-navy" data-testid={`repeat-row-${i}`}>
          <span>{[r[first], r[q.fields[1]?.key]].filter(Boolean).join(" · ")}</span>
          <button onClick={() => setRows(rows.filter((_, j) => j !== i))} className="text-coral" aria-label={t("remove")}><Trash2 size={14} /></button>
        </div>
      ))}
      <div className="mt-3 border border-navy/15 bg-white p-4">
        <Fields fields={q.fields} value={draft} onChange={setDraft} prefix="rep" />
        <button onClick={addRow} className={`${line} mt-4`} data-testid="repeat-add"><Plus size={14} /> {t("add")}</button>
      </div>
      <button onClick={() => { const all = Object.values(draft).some(Boolean) ? [...rows, draft] : rows; onSubmit(all); }} disabled={busy} className={`${primary} mt-5`} data-testid="interview-next">{t("continue")}</button>
    </div>
  );
}

export function Typed({ q, value, onSubmit, onSkip, busy }) {
  const { t, pick } = useLocale();
  const [v, setV] = useState(value ?? "");
  const [g, setG] = useState(value && typeof value === "object" ? value : {});
  const submit = () => onSubmit(q.type === "group" ? g : v);
  const words = String(v).trim() ? String(v).trim().split(/\s+/).length : 0;
  return (
    <form onSubmit={(e) => { e.preventDefault(); submit(); }}>
      {q.type === "group" && <Fields fields={q.fields} value={g} onChange={setG} prefix="grp" />}
      {q.type === "textarea" && <textarea rows={6} className={input} value={v} onChange={(e) => setV(e.target.value)} placeholder={q.example || ""} data-testid="interview-input" />}
      {["text", "number", "date"].includes(q.type) && (
        <input type={q.type === "date" ? "date" : "text"} inputMode={q.type === "number" ? "decimal" : undefined} className={input} value={v}
          onChange={(e) => setV(e.target.value)} placeholder={q.example || ""} autoFocus data-testid="interview-input" />
      )}
      {q.type === "textarea" && <p className="mt-1 text-xs text-muted">{words}</p>}
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button type="submit" disabled={busy} className={primary} data-testid="interview-next">{busy ? t("saving") : t("continue")}</button>
        {!q.required && <button type="button" onClick={onSkip} className={line} data-testid="interview-skip">{t("skip")}</button>}
      </div>
    </form>
  );
}
